import { useMemo } from "react";
import { ConnectProto, ContractStatus } from "@racoon/api";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { parseIdList } from "@racoon/ui";
import { useListContractsQuery } from "~/shared/api/contract/contract.grpc";
import { useListCounterpartiesQuery } from "~/shared/api/organization/organization.grpc";
import { useListCustomerTypesQuery } from "~/shared/api/organization-labels/organization-labels.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { DateValue } from "~/shared/lib/analytics-utils";
import { parseTariffRate } from "~/shared/lib/tariff-rate";
import { compareIds, idKey, sumOf } from "./job-report";
import { type ReportJob, useCompletedJobs } from "./use-report-data";
import {
  type ReportFilterValues,
  activeCounterpartiesArgs,
  customerTypesArgs,
  useReportFilterValues,
} from "./use-report-filters";

/** One job type priced on one of a counterparty's contracts; no job type and no amount on a contract that prices none. */
export interface CounterpartyTariff {
  contractName: string;
  jobType: string;
  /** "JOB_TYPE_*", empty with no job type. */
  jobTypeKey: string;
  amount: number | null;
}

/** One row of «Реестр контрагентов»: an active counterparty, or one of its contract tariffs. */
export interface CounterpartyReportRow {
  /** Unique among the report's rows, which can repeat a counterparty. */
  id: string;
  counterpartyId: bigint;
  bin: string;
  name: string;
  /** The provider's customer type for it; empty when it has none. */
  customerType: string;
  juridicalAddress: string;
  factAddress: string;
  /** Addresses of the counterparty's sites, "; "-separated. */
  siteAddresses: string;
  /** Containers installed on its sites, by type name: "1 × Бункер 8, 2 × Евро 1.1". */
  containers: string;
  /** m³ its completed jobs collected in the period, the counterparty's total on every one of its rows. */
  collectedVolume: number | null;
  /** The tariffs the row covers: all of the counterparty's, or the one the row is split by. */
  tariffs: CounterpartyTariff[];
}

const distinct = (values: string[]) => [...new Set(values.filter(Boolean))];

/** The contracts the row covers, by name. */
export const rowContracts = (row: CounterpartyReportRow) =>
  distinct(row.tariffs.map((tariff) => tariff.contractName)).join("; ");

export const rowJobTypes = (row: CounterpartyReportRow) =>
  distinct(row.tariffs.map((tariff) => tariff.jobType)).join("; ");

/**
 * A number on a row of one tariff, so it stays summable in Excel. Several tariffs read
 * "<job type>: <amount>" each, as the same job type can be priced on more than one contract.
 */
export const rowTariff = (row: CounterpartyReportRow): number | string => {
  const priced = row.tariffs.filter((tariff) => tariff.amount != null);
  if (priced.length === 1 && row.tariffs.length === 1) return priced[0].amount as number;
  return distinct(
    priced.map((tariff) =>
      tariff.jobType ? `${tariff.jobType}: ${tariff.amount}` : String(tariff.amount),
    ),
  ).join("; ");
};

/** The collected volume of all rows, a counterparty's once however many rows it has. */
export const totalCollectedVolume = (rows: CounterpartyReportRow[]) =>
  sumOf([...new Map(rows.map((row) => [row.counterpartyId, row.collectedVolume])).values()]);

/** The first tariff's amount, which the tariff column sorts by. */
export const rowTariffAmount = (row: CounterpartyReportRow) =>
  row.tariffs.find((tariff) => tariff.amount != null)?.amount ?? null;

/** The contract's name; one saved without a name is known by its deal number. */
const contractName = (contract: ConnectProto.Contract) => contract.name || contract.dealNumber;

/** What a contract prices: a tariff per job type, else the single legacy tariff, else nothing. */
const contractTariffs = (
  contract: ConnectProto.Contract,
  jobTypeLabel: (key: string) => string,
): CounterpartyTariff[] => {
  const name = contractName(contract);
  const byJobType = parseTariffRate(contract.tariffRateByJobType).map(
    (tariff): CounterpartyTariff => ({
      contractName: name,
      jobType: jobTypeLabel(tariff.type),
      jobTypeKey: tariff.type,
      amount: tariff.amount === "" ? null : tariff.amount,
    }),
  );
  if (byJobType.length) {
    return byJobType.sort((a, b) => a.jobType.localeCompare(b.jobType));
  }
  const legacy = Number(contract.tariff) || null;
  return [{ contractName: name, jobType: "", jobTypeKey: "", amount: legacy }];
};

interface RowsInput {
  /** Counterparties with an active contract. */
  counterparties: ConnectProto.Counterparty[];
  /** The provider's active contracts. */
  contracts: ConnectProto.Contract[];
  locations: ConnectProto.PickupLocation[];
  /** The provider's customer types, archived ones too: a counterparty keeps the one it was given. */
  customerTypes: ConnectProto.OrganizationLabel[];
  /** Completed jobs of the period. */
  jobs: Pick<ReportJob, "serviceReceiverId" | "factTotalVolume" | "jobType">[];
  jobTypeLabel: (key: string) => string;
  /**
   * The multiselect filters, each its picked ids joined by commas, "" for none. They narrow the
   * counterparties; job types also narrow the tariffs and the collected volume, container types
   * the installed containers, and a counterparty left with none of those is dropped.
   */
  filters: Pick<
    ReportFilterValues,
    "counterparties" | "customerTypes" | "jobTypes" | "containerTypes"
  >;
  /** One row per contract × job type instead of one per counterparty. */
  byContract: boolean;
}

/** The report's rows: the same facts, either one row per counterparty or split by tariff. */
export function counterpartyReportRows({
  counterparties,
  contracts,
  locations,
  customerTypes,
  jobs,
  jobTypeLabel,
  filters,
  byContract,
}: RowsInput): CounterpartyReportRow[] {
  // Nothing picked lets everything through.
  const only = (value: string) => {
    const ids = parseIdList(value);
    return (id: string) => !ids.length || ids.includes(id);
  };
  const inCounterparties = only(filters.counterparties);
  const inCustomerTypes = only(filters.customerTypes);
  const inJobTypes = only(filters.jobTypes);
  const inContainerTypes = only(filters.containerTypes);

  const volumeByReceiver = new Map<string, number | null>();
  for (const job of jobs.filter((job) => inJobTypes(job.jobType ?? ""))) {
    const key = idKey(job.serviceReceiverId);
    volumeByReceiver.set(key, sumOf([volumeByReceiver.get(key), job.factTotalVolume]));
  }

  // A site belongs to whoever owns the container groups on it.
  const addressesByOwner = new Map<string, Set<string>>();
  for (const location of locations) {
    const address = location.address?.trim();
    if (!address) continue;
    const owners = new Set(
      [
        location.ownerOrganizationId,
        ...(location.containerGroups ?? []).map((g) => g.ownerOrganizationId),
      ]
        .filter((id): id is bigint => Boolean(id))
        .map(String),
    );
    for (const owner of owners) {
      addressesByOwner.set(owner, (addressesByOwner.get(owner) ?? new Set()).add(address));
    }
  }

  // What each owner has installed: containers per container type name, over every site.
  const containersByOwner = new Map<string, Map<string, number>>();
  const groups = locations.flatMap((location) => location.containerGroups ?? []);
  for (const group of groups.filter((g) => inContainerTypes(idKey(g.containerType?.id)))) {
    const owner = idKey(group.ownerOrganizationId);
    const type = group.containerType?.name || "—";
    const byType = containersByOwner.get(owner) ?? new Map<string, number>();
    byType.set(type, (byType.get(type) ?? 0) + group.numberOfContainers);
    containersByOwner.set(owner, byType);
  }

  // Ids arrive as strings at runtime, so they are matched as strings; "0" is no type.
  const customerTypeNames = new Map(customerTypes.map((type) => [idKey(type.id), type.name]));

  const contractsByTarget = new Map<string, ConnectProto.Contract[]>();
  for (const contract of contracts) {
    const key = idKey(contract.targetOrganizationId);
    contractsByTarget.set(key, [...(contractsByTarget.get(key) ?? []), contract]);
  }

  return counterparties
    .flatMap(({ organization, customerTypeId }): CounterpartyReportRow[] => {
      if (!organization) return [];
      const key = idKey(organization.id);
      if (!inCounterparties(key) || !inCustomerTypes(idKey(customerTypeId))) return [];
      const tariffs = (contractsByTarget.get(key) ?? [])
        .sort((a, b) => contractName(a).localeCompare(contractName(b)))
        .flatMap((contract) => contractTariffs(contract, jobTypeLabel))
        .filter((tariff) => inJobTypes(tariff.jobTypeKey));
      const base = {
        counterpartyId: organization.id,
        bin: organization.bin ?? "",
        name: organization.name,
        customerType: customerTypeNames.get(idKey(customerTypeId)) ?? "",
        juridicalAddress: organization.jurAddress ?? "",
        factAddress: organization.factAddress ?? "",
        siteAddresses: [...(addressesByOwner.get(key) ?? [])]
          .sort((a, b) => a.localeCompare(b))
          .join("; "),
        containers: [...(containersByOwner.get(key) ?? [])]
          .filter(([, count]) => count)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([type, count]) => `${count} × ${type}`)
          .join(", "),
        collectedVolume: volumeByReceiver.get(key) ?? null,
      };
      if ((filters.jobTypes && !tariffs.length) || (filters.containerTypes && !base.containers)) {
        return [];
      }
      return byContract && tariffs.length
        ? tariffs.map((tariff, index) => ({ ...base, id: `${key}:${index}`, tariffs: [tariff] }))
        : [{ ...base, id: key, tariffs }];
    })
    .sort((a, b) => a.name.localeCompare(b.name) || compareIds(a.counterpartyId, b.counterpartyId));
}

/** «Реестр контрагентов» rows: the active counterparties, with what their completed jobs collected in the period. */
export function useCounterpartyReportRows(startDate: DateValue, endDate: DateValue) {
  const t = useT();
  const { activeCompanyId } = useAuthSession();
  const filters = useReportFilterValues();
  const orgId = BigInt(activeCompanyId ?? 0);
  const skip = !activeCompanyId;

  const counterparties = useListCounterpartiesQuery(activeCounterpartiesArgs(orgId), { skip });
  const contracts = useListContractsQuery(
    { serviceProviderIds: [orgId], status: ContractStatus.CONTRACT_STATUS_ACTIVE },
    { skip },
  );
  // The same list the schedule reports load, so the two share one request.
  const locations = useListPickupLocationsQuery({ serviceProviderIds: [orgId] }, { skip });
  const types = useListCustomerTypesQuery(customerTypesArgs(orgId), { skip });
  const jobs = useCompletedJobs(startDate, endDate);

  const rows = useMemo(
    () =>
      counterpartyReportRows({
        counterparties: counterparties.data?.counterparties ?? [],
        contracts: contracts.data?.contracts ?? [],
        locations: locations.data?.pickupLocations ?? [],
        customerTypes: types.data?.labels ?? [],
        jobs: jobs.rows,
        jobTypeLabel: (key) => t(Namespace.Enum, key as TranslationKey),
        filters,
        byContract: filters.counterpartyView === "contract",
      }),
    [counterparties.data, contracts.data, locations.data, types.data, jobs.rows, filters, t],
  );

  const loading = [counterparties, contracts, locations, types].some(
    (query) => query.isLoading || query.isFetching,
  );
  return {
    rows,
    isLoading: loading || jobs.isLoading,
    // The customer type is only a label: a failed list leaves its column blank, not the report.
    error: counterparties.error ?? contracts.error ?? locations.error ?? jobs.error,
  };
}
